import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { X, CheckCircle2, Clock, AlertCircle, Camera, MapPin, RefreshCw, Zap, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const STAGE_ICONS = {
  "Survey": "📋",
  "DTM (Mounting)": "🔧",
  "DTC (Connection)": "⚡",
  "MI (Commissioning)": "✅",
  "QC Inspection": "🔍"
};

const STATUS_STYLES = {
  completed: { bg: "bg-green-100", border: "border-green-500", icon: "text-green-500", dot: "bg-green-500" },
  passed: { bg: "bg-green-100", border: "border-green-500", icon: "text-green-500", dot: "bg-green-500" },
  pending: { bg: "bg-yellow-100", border: "border-yellow-500", icon: "text-yellow-500", dot: "bg-yellow-500" },
  not_started: { bg: "bg-slate-100", border: "border-slate-300", icon: "text-slate-400", dot: "bg-slate-300" },
  rejected: { bg: "bg-red-100", border: "border-red-500", icon: "text-red-500", dot: "bg-red-500" },
  skipped: { bg: "bg-slate-100", border: "border-slate-300", icon: "text-slate-400", dot: "bg-slate-300" }
};

export default function DTLifecycleModal({ dtId, onClose, onStartWork }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lifecycle, setLifecycle] = useState(null);
  const [validating, setValidating] = useState(false);
  const [geoError, setGeoError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchLifecycle();
  }, [dtId]);

  const fetchLifecycle = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/maps/dt/${dtId}/lifecycle`);
      setLifecycle(res.data);
    } catch (e) {
      toast.error("Failed to load lifecycle data");
    } finally {
      setLoading(false);
    }
  };

  const handleStartWork = async () => {
    if (!lifecycle?.dt || !lifecycle.next_action?.route) return;
    
    const dt = lifecycle.dt;
    setValidating(true);
    setGeoError(null);
    
    try {
      // Run geo-fence validation
      await onStartWork(dtId, dt.gps_lat, dt.gps_lng);
      
      // Navigate to the appropriate form
      navigate(`${lifecycle.next_action.route}/${dtId}`);
      onClose();
    } catch (e) {
      setGeoError(e.message);
      toast.error(e.message);
    } finally {
      setValidating(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={onClose}>
        <div className="bg-white w-full max-w-lg rounded-t-2xl p-6 animate-slide-up" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-center py-12">
            <RefreshCw size={32} className="animate-spin text-orange-500" />
          </div>
        </div>
      </div>
    );
  }

  if (!lifecycle) return null;

  const { dt, timeline, next_action, color } = lifecycle;
  const styles = STATUS_STYLES[lifecycle.current_status] || STATUS_STYLES.not_started;

  return (
    <>
      {/* Bottom Sheet Modal */}
      <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={onClose}>
        <div 
          className="bg-white w-full max-w-lg rounded-t-2xl shadow-2xl max-h-[85vh] overflow-hidden animate-slide-up"
          onClick={e => e.stopPropagation()}
          data-testid="lifecycle-modal"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
            <div>
              <h2 className="font-bold text-lg text-slate-900">{dt?.dt_code}</h2>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span>{dt?.transformer_capacity_kva} kVA</span>
                <span>•</span>
                <span>{dt?.circle_name}</span>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full">
              <X size={20} className="text-slate-500" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto p-4 max-h-[calc(85vh-180px)]">
            {/* Current Status Badge */}
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold mb-4 ${styles.bg}`} style={{ color }}>
              <span className={`w-2 h-2 rounded-full ${styles.dot}`} />
              {next_action?.label || "Unknown Status"}
            </div>

            {/* Timeline */}
            <div className="space-y-4">
              {timeline?.map((stage, index) => {
                const stageStyles = STATUS_STYLES[stage.status] || STATUS_STYLES.not_started;
                const isLast = index === timeline.length - 1;
                
                return (
                  <div key={index} className="relative">
                    {/* Connecting Line */}
                    {!isLast && (
                      <div className={`absolute left-4 top-10 w-0.5 h-full -bottom-4 ${stageStyles.dot}`} />
                    )}
                    
                    {/* Stage Card */}
                    <div className={`border-l-4 ${stageStyles.border} bg-white rounded-sm p-3 ml-2 shadow-sm`}>
                      {/* Stage Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{STAGE_ICONS[stage.stage]}</span>
                          <span className="font-bold text-slate-800">{stage.stage}</span>
                        </div>
                        {stage.status === "completed" || stage.status === "passed" ? (
                          <CheckCircle2 size={18} className="text-green-500" />
                        ) : stage.status === "pending" ? (
                          <Clock size={18} className="text-yellow-500" />
                        ) : stage.status === "rejected" ? (
                          <AlertCircle size={18} className="text-red-500" />
                        ) : null}
                      </div>

                      {/* Stage Details */}
                      {stage.datetime && (
                        <div className="text-xs text-slate-500 mb-2">
                          {formatDateTime(stage.datetime)} • By: {stage.performed_by || "N/A"}
                        </div>
                      )}

                      {/* Stage Data */}
                      {stage.data && Object.keys(stage.data).length > 0 && (
                        <div className="bg-slate-50 rounded p-2 text-xs space-y-1 mb-2">
                          {Object.entries(stage.data).filter(([_, v]) => v).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="text-slate-500">{key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}:</span>
                              <span className={`font-medium ${key === "reject_reason" ? "text-red-600" : "text-slate-700"}`}>{value}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Photos */}
                      {stage.photos && stage.photos.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-1">
                          {stage.photos.map((photo, i) => (
                            <button
                              key={i}
                              onClick={() => setSelectedImage(photo)}
                              className="flex-shrink-0 w-16 h-16 bg-slate-200 rounded overflow-hidden hover:opacity-80"
                            >
                              <img src={photo} alt={`${stage.stage} photo ${i + 1}`} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}

                      {/* GPS Badge */}
                      {stage.gps?.lat && (
                        <div className="flex items-center gap-1 text-xs text-slate-400 mt-2">
                          <MapPin size={12} />
                          <span>{stage.gps.lat.toFixed(4)}, {stage.gps.lng.toFixed(4)}</span>
                        </div>
                      )}

                      {/* Shutdown Required Warning */}
                      {stage.requires_shutdown && stage.status === "pending" && (
                        <div className="flex items-center gap-1 text-xs text-orange-600 mt-2 font-medium">
                          <Zap size={12} />
                          <span>Requires Active Shutdown</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer - Start Work Button */}
          <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4">
            {geoError && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-3 text-sm text-red-700 flex items-start gap-2">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold">Geo-Fence Failed</div>
                  <div>{geoError}</div>
                </div>
              </div>
            )}

            {next_action?.route ? (
              <button
                onClick={handleStartWork}
                disabled={validating}
                className="w-full py-3 bg-orange-500 text-white rounded-sm font-bold text-sm flex items-center justify-center gap-2 hover:bg-orange-600 disabled:opacity-60"
                data-testid="start-work-btn"
              >
                {validating ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Validating Location...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={18} />
                    Check-In / {next_action.label}
                    <ChevronRight size={18} />
                  </>
                )}
              </button>
            ) : (
              <div className="text-center text-slate-500 text-sm py-2">
                {next_action?.label || "No action available"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-slate-300"
            onClick={() => setSelectedImage(null)}
          >
            <X size={32} />
          </button>
          <img 
            src={selectedImage} 
            alt="Full size" 
            className="max-w-full max-h-full object-contain rounded"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
